/* Popover button component */

import React from 'react';

import box from 'Style/box';

import CHAR_CODES from '../../../utils/char_codes';

function getStyle() {
  const popoverBase = {
    position: 'absolute' as const,
    top: '100%',
    zIndex: 200,
    overflow: 'auto',
    border: '1px solid rgb(230, 230, 230)',
    ...box.popover,
  };

  return {
    container: {
      position: 'relative' as const,
    },
    containerLeft: {
      // counterintuitive but better for now than changing popover input form default side
      position: 'relative' as const,
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    getPopover(side: TProps['side'], extraStylePop?: React.CSSProperties) {
      const popover = {
        ...popoverBase,
        ...extraStylePop, // page
      };
      if (side === 'left') {
        popover.right = 0;
      }
      return popover;
    },
    trigger: {
      height: '100%',
    },
  };
}

type TProps = {
  defaultIsOpen?: boolean;
  extraStyle?: React.CSSProperties;
  extraStylePop?: React.CSSProperties;
  onClose?: () => void;
  onOpen?: () => void;
  popover: React.ReactNode;
  side: 'left' | 'right';
  trigger: React.ReactNode;
};

type TState = {
  isOpen: boolean;
};

class PopoverButton extends React.Component<TProps, TState> {
  static defaultProps = {
    side: 'right',
  };

  containerRef = React.createRef<HTMLDivElement>();

  constructor(props: TProps) {
    super(props);
    this.state = {
      isOpen: props.defaultIsOpen || false,
    };
  }

  override componentDidMount(): void {
    document.addEventListener('click', this.onAnyClick);
    document.addEventListener('keyup', this.onKeyUp);
  }

  override componentWillUnmount(): void {
    document.removeEventListener('click', this.onAnyClick);
    document.removeEventListener('keyup', this.onKeyUp);
  }

  onAnyClick = (event: MouseEvent): void => {
    const target = event.target; // init
    if (
      !this.state.isOpen ||
      !(target instanceof Node) ||
      !document.contains(target)
    ) {
      return;
    }

    const isClickedOutside = !this.containerRef.current?.contains(target);

    if (isClickedOutside) {
      this.close();
    }
  };

  onKeyUp = (event: KeyboardEvent): void => {
    if (!this.state.isOpen) {
      return;
    }
    if (event.keyCode === CHAR_CODES.ESCAPE) {
      this.close();
    }
  };

  handleTriggerClick = (): void => {
    if (this.state.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  open(): void {
    this.setState(
      {
        isOpen: true,
      },
      () => this.props.onOpen?.(),
    );
  }

  close(): void {
    // also public
    this.setState(
      {
        isOpen: false,
      },
      () => {
        if (this.props.onClose) {
          this.props.onClose();
        }
      },
    );
  }

  override render(): React.ReactNode {
    const {trigger, popover, side, extraStyle, extraStylePop} = this.props;
    const {isOpen} = this.state;
    const popoverStyle = isOpen
      ? getStyle().getPopover(side, extraStylePop)
      : undefined;
    const popoverDiv = isOpen ? (
      <div style={popoverStyle}>{popover}</div>
    ) : null;
    const containerStyle =
      side === 'left'
        ? {
            ...getStyle().containerLeft,
            ...extraStyle,
          }
        : {
            ...getStyle().container,
            ...extraStyle,
          };
    return (
      <div style={containerStyle} ref={this.containerRef}>
        <div style={getStyle().trigger} onClick={this.handleTriggerClick}>
          {trigger}
        </div>
        {popoverDiv}
      </div>
    );
  }
}

export default PopoverButton;
